import React, { useMemo } from 'react'
import cn from 'clsx'
import OfferPrice from './Price/OfferPrice'
import { PriceContainer, PricingWrapper, SingleValueWrapper } from './styles'
import { connect } from 'react-redux'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import Pill from 'components/Luxkit/Pill'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { isVillaOffer } from 'lib/offer/offerTypes'
import { getCapacityTitle } from 'lib/homesAndVillas/getCapacityTitle'

interface Props {
  offer: App.Offer | App.OfferSummary;
  className?: string;
  infoBarMedium?: boolean;
  infoBarMini?: boolean;
  infoBar?: boolean;
  showFlights?: boolean;
  originAirports: Array<App.Airport>;
  flightOrigin?: string;
}

function OfferPricing(props: Props) {
  const {
    originAirports,
    infoBarMedium,
    infoBarMini,
    className,
    infoBar,
    showFlights,
    offer,
    flightOrigin,
  } = props

  const {
    tileDurationLabel,
    parentType,
    type,
    typeLabel,
    saleUnit,
    offerFlightsEnabled,
    flightCacheDisabled,
    property,
    lowestPricePackage,
  } = offer

  const {
    name,
    roomOccupancy,
    shouldDisplayValue,
    discountPercent,
    roomType,
    roomPolicyDescription,
  } = lowestPricePackage

  const noFlightsFromOrigin = offer.flightDestinationPort === flightOrigin
  const originAirport = useMemo(() => originAirports.find(a => a.code === flightOrigin), [originAirports, flightOrigin])
  const flightCacheOff = showFlights && (flightCacheDisabled || !offer.flightPrices[flightOrigin])

  const bundledPrice = lowestPricePackage.price + (offer.flightPrices[flightOrigin] || 0)
  const bundledValue = lowestPricePackage.value + (offer.flightPrices[flightOrigin] || 0)

  const hotelDiscountPercent = getValueOffPercent(lowestPricePackage.value, lowestPricePackage.price)
  const bundledDiscountPercent = getValueOffPercent(bundledValue, bundledPrice)
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent
  const shouldShowDiscountPercent = !offer.isDiscountPillHidden && !offer.bundledWithFlightsOnly && highestDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const isCruise = offer.holidayTypes.includes('Cruises')
  const isHomesAndVillas = isVillaOffer(offer)
  const capacityLabel = isHomesAndVillas ? getCapacityTitle(offer) : null

  return <>
    {shouldShowDiscountPercent && !isCruise &&
      <SingleValueWrapper variant="medium" weight="bold">
        <Pill kind="primary" variant="success">Save up to {highestDiscountPercent}%</Pill>
      </SingleValueWrapper>
    }
    <PricingWrapper>
      {!offer.bundledWithFlightsOnly && <PriceContainer className={cn(className, {
        'cache-off': flightCacheOff && offerFlightsEnabled,
      })}>
        <OfferPrice
          offer={offer}
          className={className}
          durationLabel={tileDurationLabel}
          infoBarMedium={infoBarMedium}
          infoBarMini={infoBarMini}
          flightCacheOff={flightCacheOff}
          roomOccupancy={isCruise ? name : parentType === OFFER_TYPE_TOUR ? roomOccupancy : undefined}
          offerSaleUnit={saleUnit}
          label={capacityLabel ?? typeLabel}
          shouldShowValue={shouldDisplayValue}
          showFlights={showFlights}
          price={lowestPricePackage.price}
          taxesAndFees={lowestPricePackage.taxesAndFees}
          value={lowestPricePackage.value}
          discountPercent={discountPercent}
          type={type}
          roomType={roomType}
          maxChildAge={property?.maxChildAge}
          maxInfantAge={property?.maxInfantAge}
          roomPolicyDescription={roomPolicyDescription}
          availablePackage={lowestPricePackage}
          highestDiscountPercent={highestDiscountPercent}
          isCruise={isCruise}
        />
      </PriceContainer>}
      {offerFlightsEnabled && showFlights && <PriceContainer className={cn(className, {
        'cache-off': flightCacheOff && offerFlightsEnabled,
      })}>
        <OfferPrice
          offer={offer}
          hasClause
          isFlightPrice
          className={className}
          durationLabel={tileDurationLabel}
          flightCacheOff={flightCacheOff}
          infoBarMedium={infoBarMedium}
          infoBarMini={infoBarMini}
          infoBar={infoBar}
          offerSaleUnit="package"
          label={`${typeLabel} + Flights`}
          shouldShowValue={shouldDisplayValue && !flightCacheOff}
          showFlights={showFlights}
          price={bundledPrice}
          taxesAndFees={lowestPricePackage.taxesAndFees}
          value={bundledValue}
          originAirport={originAirport}
          noFlightsFromOrigin={noFlightsFromOrigin}
          discountPercent={discountPercent}
          roomType={roomType}
          maxChildAge={property?.maxChildAge}
          maxInfantAge={property?.maxInfantAge}
          roomPolicyDescription={roomPolicyDescription}
          availablePackage={lowestPricePackage}
          type={type}
          highestDiscountPercent={highestDiscountPercent}
          isCruise={isCruise}
        />
      </PriceContainer>}
    </PricingWrapper>
  </>
}

function mapStateToProps(state: App.State) {
  return {
    originAirports: state.geo.originAirports,
  }
}

export default connect(mapStateToProps)(OfferPricing)
